import { UserRoleEnum } from "@/api/user/models";
import { getScheduleAgenda, getScheduleStageCounts, updateSchedulePresence } from "@/api/schedule";
import {
  PhotoService,
  ScheduleType,
  type ScheduleStage as ApiScheduleStage,
  type ScheduledSchedule,
  type ScheduleStatus,
} from "@/api/schedule/models";
import { EmptyLayout } from "@/components/common/EmptyLayout";
import { ModalAlert } from "@/components/common/modals";
import { Tabs, type Tab } from "@/components/common/Tabs";
import { CreateScheduleModal } from "@/components/features/schedule/modals/CreateScheduleModal";
import { ProducerAvailabilityModal } from "@/components/features/schedule/modals/ProducerAvailabilityModal";
import { ScheduleActionsHeader } from "@/components/features/schedule/ScheduleActionsHeader";
import { ScheduleTable } from "@/components/features/schedule/ScheduleTable";
import { useDepartmentRoute } from "@/hooks/common/use-department-route";
import { useFetch } from "@/hooks/common/use-fetch";
import { useModal } from "@/hooks/common/use-modal";
import type { OrderDirection } from "@/interfaces/api";
import { showToast } from "@/providers";
import { formatDateInSaoPauloTimeZone } from "@/utils/date";
import { SCHEDULE_STATUS_LABEL_BY_STATUS } from "@/constants/schedule";
import { getUserRoleByDepartment } from "@/utils/user-positions";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useUserStore } from "@/stores/use-user-store";
import { useSearchParams } from "react-router-dom";

import {
  OPERATIONAL_SCHEDULE_STAGE_BY_ROLE,
  backendStageByScheduleStage,
  scheduleColumnsByStage,
  scheduleStageDefinitions,
  type ScheduleStage,
} from "./tableValues";

export const shouldShowScheduleEmptyLayout = (
  isSuccessful: boolean,
  resultCount: number,
  hasActiveFilters: boolean,
): boolean => isSuccessful && resultCount === 0 && !hasActiveFilters;

export const SchedulePage = (): JSX.Element => {
  const [searchParams] = useSearchParams();
  const requestedDate = searchParams.get("date");
  const activeUser = useUserStore((state) => state.activeUser);
  const updateProducerAvailability = useUserStore((state) => state.updateProducerAvailability);
  const consumeShouldPromptAvailability = useUserStore((state) => state.consumeShouldPromptAvailability);
  const { department } = useDepartmentRoute();
  const activeRole = getUserRoleByDepartment(activeUser, department);
  const operationalStage = activeRole ? OPERATIONAL_SCHEDULE_STAGE_BY_ROLE[activeRole] : undefined;
  const [selectedDate, setSelectedDate] = useState(requestedDate ?? formatDateInSaoPauloTimeZone());
  const [activeStage, setActiveStage] = useState<ScheduleStage>("scheduled");
  const currentStage = operationalStage ?? activeStage;
  const [page, setPage] = useState(1);
  const [scheduleTypes, setScheduleTypes] = useState<ScheduleType[] | undefined>();
  const [photoServices, setPhotoServices] = useState<PhotoService[] | undefined>();
  const [lastStages, setLastStages] = useState<ApiScheduleStage[] | undefined>();
  const [statuses, setStatuses] = useState<ScheduleStatus[] | undefined>();
  const [scheduleDateOrder, setScheduleDateOrder] = useState<OrderDirection>("asc");
  const isReception = currentStage === "reception";
  const isToday = selectedDate === formatDateInSaoPauloTimeZone();
  const requestedScheduleTypes = scheduleTypes;
  const requestedPhotoServices = photoServices;
  const requestedLastStages = lastStages;
  const requestedStatuses = statuses;
  const requestedScheduleDateOrder = scheduleDateOrder.toUpperCase() as "ASC" | "DESC";
  const { closeModal, isModalOpen, openModal } = useModal();
  const availabilityModal = useModal<{ isAvailable: boolean }>();
  const removePresenceModal = useModal<ScheduledSchedule>();
  const producerPosition = activeUser?.positions.find(
    (position) => position.department === department && position.role === UserRoleEnum.PRODUCER,
  );
  const isProducerUnavailable = producerPosition?.isAvailable === false;

  useEffect(() => {
    if (!producerPosition) {
      return;
    }

    if (consumeShouldPromptAvailability() && !producerPosition.isAvailable) {
      availabilityModal.openModal({ isAvailable: true });
    }
  }, [availabilityModal, consumeShouldPromptAvailability, producerPosition]);

  const queryClient = useQueryClient();
  const stageCounts = useFetch(() => getScheduleStageCounts(selectedDate ?? undefined), {
    queryKey: ["schedule", "stage-counts", selectedDate],
    includeDepartmentInCache: true,
  });
  const scheduledSchedules = useFetch(
    () =>
      getScheduleAgenda(
        backendStageByScheduleStage[currentStage],
        selectedDate ?? undefined,
        page,
        8,
        requestedScheduleTypes,
        requestedPhotoServices,
        requestedLastStages,
        requestedStatuses,
        requestedScheduleDateOrder,
      ),
    {
      queryKey: [
        "schedule",
        "agenda",
        backendStageByScheduleStage[currentStage],
        selectedDate,
        requestedScheduleTypes,
        requestedPhotoServices,
        requestedLastStages,
        requestedStatuses,
        requestedScheduleDateOrder,
        page,
      ],
      includeDepartmentInCache: true,
    },
  );
  const updatePresenceMutation = useMutation({
    mutationFn: ({ scheduleId, isPresent }: { scheduleId: string; isPresent: boolean }) =>
      updateSchedulePresence(scheduleId, { isPresent }),
    onSuccess: async (_, { scheduleId, isPresent }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["schedule", "agenda"], refetchType: "active" }),
        queryClient.invalidateQueries({ queryKey: ["schedule", "stage-counts"], refetchType: "active" }),
        queryClient.invalidateQueries({
          queryKey: ["schedule", "detail", scheduleId],
          refetchType: "active",
        }),
        queryClient.invalidateQueries({
          queryKey: ["schedule", "talent-data", scheduleId],
          refetchType: "active",
        }),
      ]);
      await scheduledSchedules.refetch();
      showToast({
        type: "success",
        title: isPresent ? "Presença confirmada!" : "Presença desmarcada.",
      });
    },
    onError: (_, { isPresent }) => {
      showToast({
        type: "error",
        title: isPresent ? "Não foi possível confirmar presença." : "Não foi possível desmarcar presença.",
      });
    },
  });
  const canUpdatePresence = isToday;
  const handlePresenceChange = (schedule: ScheduledSchedule) => {
    if (schedule.isPresent) {
      removePresenceModal.openModal(schedule);
      return;
    }

    updatePresenceMutation.mutate({ scheduleId: schedule.scheduleId, isPresent: true });
  };

  const handleRemovePresence = () => {
    updatePresenceMutation.mutate(
      { scheduleId: removePresenceModal.state.scheduleId, isPresent: false },
      { onSuccess: removePresenceModal.closeModal },
    );
  };
  const scheduleStages = useMemo<Tab[]>(
    () =>
      scheduleStageDefinitions.map(({ label, value }) => ({
        label: `${label} (${stageCounts.data?.[backendStageByScheduleStage[value]] ?? 0})`,
        value,
      })),
    [stageCounts.data],
  );
  const categoryFilter = {
    isActive: scheduleTypes !== undefined,
    options: [
      {
        label: "Renovação de fotos",
        value: ScheduleType.PHOTO_RENEWAL,
        isSelected: scheduleTypes === undefined || scheduleTypes.includes(ScheduleType.PHOTO_RENEWAL),
      },
      {
        label: "Novo candidato",
        value: ScheduleType.NEW_CANDIDATE,
        isSelected: scheduleTypes === undefined || scheduleTypes.includes(ScheduleType.NEW_CANDIDATE),
      },
    ],
    onValuesChange: (values: string[]) => {
      setScheduleTypes(values as ScheduleType[]);
      setPage(1);
    },
  };
  const photoServiceFilter = {
    isActive: photoServices !== undefined,
    options: [
      {
        label: "Material fotográfico",
        value: "PHOTO_MATERIAL",
        isSelected: photoServices === undefined || photoServices.includes("PHOTO_MATERIAL"),
      },
      {
        label: "Fotogenia",
        value: "PHOTOGENY",
        isSelected: photoServices === undefined || photoServices.includes("PHOTOGENY"),
      },
    ],
    onValuesChange: (values: string[]) => {
      setPhotoServices(values as PhotoService[]);
      setPage(1);
    },
  };
  const lastStageFilter = {
    isActive: lastStages !== undefined,
    options: [
      {
        label: "Recepção",
        value: "RECEPTION",
        isSelected: lastStages === undefined || lastStages.includes("RECEPTION"),
      },
      {
        label: "Fotografia",
        value: "PHOTOGRAPHY",
        isSelected: lastStages === undefined || lastStages.includes("PHOTOGRAPHY"),
      },
      {
        label: "Produção",
        value: "PRODUCTION",
        isSelected: lastStages === undefined || lastStages.includes("PRODUCTION"),
      },
      {
        label: "Produção artística",
        value: "ART_PRODUCTION",
        isSelected: lastStages === undefined || lastStages.includes("ART_PRODUCTION"),
      },
    ],
    onValuesChange: (values: string[]) => {
      setLastStages(values as ApiScheduleStage[]);
      setPage(1);
    },
  };
  const statusFilter = {
    isActive: statuses !== undefined,
    options: [
      {
        label: SCHEDULE_STATUS_LABEL_BY_STATUS.AWAITING,
        value: "AWAITING",
        isSelected: statuses === undefined || statuses.includes("AWAITING"),
      },
      {
        label: SCHEDULE_STATUS_LABEL_BY_STATUS.IN_PROGRESS,
        value: "IN_PROGRESS",
        isSelected: statuses === undefined || statuses.includes("IN_PROGRESS"),
      },
      {
        label: SCHEDULE_STATUS_LABEL_BY_STATUS.PAUSED,
        value: "PAUSED",
        isSelected: statuses === undefined || statuses.includes("PAUSED"),
      },
      {
        label: SCHEDULE_STATUS_LABEL_BY_STATUS.CANCELED,
        value: "CANCELED",
        isSelected: statuses === undefined || statuses.includes("CANCELED"),
      },
      {
        label: SCHEDULE_STATUS_LABEL_BY_STATUS.COMPLETED,
        value: "COMPLETED",
        isSelected: statuses === undefined || statuses.includes("COMPLETED"),
      },
    ],
    onValuesChange: (values: string[]) => {
      setStatuses(values as ScheduleStatus[]);
      setPage(1);
    },
  };
  const timeSort = {
    isActive: true,
    direction: scheduleDateOrder,
    onChange: () => {
      setScheduleDateOrder((currentOrder) => (currentOrder === "asc" ? "desc" : "asc"));
      setPage(1);
    },
  };
  const hasActiveFilters = [scheduleTypes, photoServices, lastStages, statuses].some((values) => values !== undefined);
  const hasNoUnfilteredScheduledResults = shouldShowScheduleEmptyLayout(
    scheduledSchedules.isSuccess,
    scheduledSchedules.data?.data.length ?? 0,
    hasActiveFilters,
  );

  const availableTabs = useMemo(() => {
    if (isToday) {
      return scheduleStages;
    }

    return scheduleStages.filter(({ value }) => value === "scheduled");
  }, [isToday, scheduleStages]);

  const scheduleContent = (() => {
    if (isProducerUnavailable) {
      return (
        <div className="flex min-h-[calc(100vh-20rem)] items-center">
          <EmptyLayout title="Sua disponibilidade está desativada. Ative para gerenciar atendimentos." />
        </div>
      );
    }

    if (hasNoUnfilteredScheduledResults) {
      return (
        <div className="flex min-h-[calc(100vh-20rem)] items-center">
          <EmptyLayout
            title="Nenhum resultado no momento"
            description="Os talentos aparecerão aqui quando forem agendados ou enviados para esta etapa."
          />
        </div>
      );
    }

    return (
      <ScheduleTable
        schedules={scheduledSchedules.data?.data ?? []}
        columns={scheduleColumnsByStage[currentStage]}
        pagination={scheduledSchedules.data ?? { data: [], total: 0, page, limit: 8, pageCount: 0 }}
        isLoading={scheduledSchedules.isPendingOrErrored}
        showPresence={isToday && (currentStage === "scheduled" || isReception)}
        categoryFilter={categoryFilter}
        photoServiceFilter={photoServiceFilter}
        lastStageFilter={lastStageFilter}
        statusFilter={statusFilter}
        timeSort={timeSort}
        canUpdatePresence={canUpdatePresence}
        pendingScheduleId={
          updatePresenceMutation.isPending ? (updatePresenceMutation.variables?.scheduleId ?? null) : null
        }
        onPageChange={setPage}
        onPresenceChange={handlePresenceChange}
      />
    );
  })();

  return (
    <>
      <ScheduleActionsHeader
        onCreateSchedule={openModal}
        onToggleAvailability={(isAvailable) => availabilityModal.openModal({ isAvailable })}
        selectedDate={selectedDate}
        stage={backendStageByScheduleStage[currentStage]}
        onSelectDate={(date) => {
          setSelectedDate(date);
          setActiveStage(operationalStage ?? "scheduled");
          setPage(1);
        }}
      />

      <div className="mt-6 flex flex-col gap-6">
        {operationalStage === undefined && (
          <Tabs
            activeTab={activeStage}
            onTabChange={(stage) => {
              const selectedStage = stage as ScheduleStage;
              setActiveStage(selectedStage);
              setPage(1);
            }}
            tabs={availableTabs}
          />
        )}
        {scheduleContent}
      </div>

      <CreateScheduleModal isOpen={isModalOpen} onClose={closeModal} />
      {availabilityModal.isModalOpen && (
        <ProducerAvailabilityModal
          isOpen
          isAvailable={availabilityModal.state.isAvailable}
          onClose={availabilityModal.closeModal}
          onSuccess={(isAvailable) => {
            if (!department) {
              return;
            }

            updateProducerAvailability(department, isAvailable);
          }}
        />
      )}

      <ModalAlert
        isOpen={removePresenceModal.isModalOpen}
        onClose={removePresenceModal.closeModal}
        alertIcon={{ variant: "error", icon: "Cancel" }}
        title="Deseja remover a presença?"
        description="Se a presença for removida, o talento voltará para a aba de Agendados, com status pendente."
        actions={[
          ["Voltar", removePresenceModal.closeModal],
          {
            children: "Sim, remover presença",
            variant: "destructive",
            className: "!w-fit !max-w-none whitespace-nowrap",
            leading: "Close",
            isLoading: updatePresenceMutation.isPending,
            onClick: handleRemovePresence,
          },
        ]}
      />
    </>
  );
};
